import type {
  ICurrency,
  IInquiryLoadTimeRegistration,
  INewQuoteForm,
  ITravelPointQuote,
} from '../../types'

import { useUpFetch } from '../../utils'

export const useInquiryApi = () => {
  const upFetch = useUpFetch()

  const deleteInquiryLoad = async (loadId: number) => {
    return upFetch(`/inquiry-loads/${loadId}`, {
      method: 'DELETE',
    })
  }

  const fetchInquiry = async (inquiryLoadId?: number) => {
    if (!inquiryLoadId) return

    const attributes = [
      'load.documents',
      'load.chat',
      'load.drops',
      'load.load_type',
      'load.packaging_type',
      'load.unload_time',
      'load.load_time',
      'load.registration_plates',
      'load.rates',
    ].join('|')

    return upFetch<IInquiryLoadTimeRegistration>(`/inquiry-loads/${inquiryLoadId}`, {
      params: { i: attributes },
    })
  }

  const updateInquiryStatus = async (loadId: number, body: { status: 'expired' | string }) => {
    return upFetch(`/inquiry-loads/${loadId}/status`, {
      body,
      method: 'PATCH',
    })
  }

  //todo this should be redone
  const updateInquiryLoad = async (data: Partial<INewQuoteForm & { reset_bidding: boolean }>) => {
    return await upFetch<{ data: { id: number } }>(`inquiry-loads/${data.id}`, {
      body: data,
      method: 'PATCH',
    })
  }

  const createNewInquiryLoad = async (data: INewQuoteForm, token: null | string | undefined) => {
    return upFetch<{ data: { id: number } }>(`/inquiry-loads`, {
      body: data,
      headers: {
        Authorization: `Bearer ${token}`,
      },
      method: 'POST',
    })
  }

  const postConvertQuoteToLoad = async (inquiryId: number, rate: number) => {
    try {
      await upFetch(`/inquiry-loads/${inquiryId}/bidding/customer/book`, {
        body: {
          customer_rate: rate,
          rate,
        },
        method: 'POST',
      })

      return true
    } catch (error) {
      return false
    }
  }

  const patchInquiryLoad = async (
    inquiryId: number,
    body: { body: Record<string, unknown>; id: number },
  ) =>
    upFetch(`/inquiry-loads/${inquiryId}`, {
      body,
      method: 'PATCH',
    })

  const patchInquiryLoadState = async (inquiryId: number, body: { state: string }) =>
    upFetch(`/inquiry-loads/${inquiryId}/state`, {
      body,
      method: 'PATCH',
    })

  const patchInquiryLoadStatus = async (inquiryId: number, body: { status: string }) =>
    upFetch(`/inquiry-loads/${inquiryId}/status`, {
      body,
      method: 'PATCH',
    })

  const postBid = async (
    inquiryId: number,
    data: {
      bid: {
        amount: number
        currency: ICurrency
      }
      negotiable: boolean
    },
  ) => {
    return upFetch<{ data: boolean }>(`/inquiry-loads/${inquiryId}/bidding/admin/bid`, {
      body: data,
      method: 'POST',
    })
  }

  const updateLoadPreferredRate = async (
    loadId: number,
    preferred_rate: number,
    currency: 'EUR' | 'GBP' | 'PLN' | 'USD',
  ) => {
    return upFetch<{ data: boolean }>(`/loads/${loadId}/rates/preferred-rate`, {
      body: {
        currency,
        preferred_rate,
      },
      method: 'PATCH',
    })
  }

  const postConvertInquiryToLoad = async (inquiryId: number, rate: number) => {
    return await upFetch<{ data: boolean }>(`/inquiry-loads/${inquiryId}/bidding/customer/book`, {
      body: {
        customer_rate: rate,
        rate,
      },
      method: 'POST',
    })
  }

  const confirmBooking = async (inquiryId: number) => {
    return upFetch<{ data: boolean }>(`/inquiry-loads/${inquiryId}/confirm`, {
      method: 'PATCH',
    })
  }

  const postInquiryVerify = async (inquiryId: number) => {
    return upFetch<{ data: boolean }>(`/inquiry-loads/${inquiryId}/verify`, {
      method: 'POST',
    })
  }

  const postInquiryAskTeamForRate = async (inquiryId: number) => {
    return upFetch<{ data: boolean }>(`/inquiry-loads/${inquiryId}/ask-rate`, {
      method: 'POST',
    })
  }

  const editQuote = async (data: INewQuoteForm) => {
    return upFetch<{ data: { id: number } }>(`/inquiry-loads/${data.id}`, {
      body: data,
      method: 'PATCH',
    })
  }

  const updateLoadDrops = async ({
    drops,
    loadId,
    resetBidding = false,
  }: {
    drops: ITravelPointQuote[]
    loadId: number
    resetBidding?: boolean
  }) => {
    return upFetch<{ data: { id: number } }>(`/loads/${loadId}/drops`, {
      body: { drops, reset_bidding: resetBidding },
      method: 'POST',
    })
  }

  return {
    confirmBooking,
    createNewInquiryLoad,
    deleteInquiryLoad,
    editQuote,
    fetchInquiry,
    patchInquiryLoad,
    patchInquiryLoadState,
    patchInquiryLoadStatus,
    postBid,
    postConvertInquiryToLoad,
    postConvertQuoteToLoad,
    postInquiryAskTeamForRate,
    postInquiryVerify,
    updateInquiryLoad,
    updateInquiryStatus,
    updateLoadDrops,
    updateLoadPreferredRate,
  }
}
